import { Injectable } from '@angular/core';
// import * as L from 'leaflet'; // ensure this is at top

export interface FeedUI {           // the hint text
  showFilterIcon: boolean;   
}

export interface FeedViewModel {
  sections: FeedSection[];
  ui: FeedUI;
}

export interface FeedLine {
  text: string;
  legendColor?: string;
}
export interface FeedSection {
  show: boolean;
  className: string;
  style?: { [k: string]: string };
  items: FeedLine[];
}
export interface BuildFeedSectionsMeta {
  featureCount?: number;
  showCountRow?: boolean;   // <= only override when this is true
}

@Injectable({ providedIn: 'root' })
export class HelperService {

  private safeAssign(
  label: string,
  value: unknown,
  suffix = '',
  extraVisible?: boolean,     // new flag
  labelWhenEmpty?: string     // optional override label when no value
): string {
  const hasValue = value !== null && value !== undefined && value !== '';
  if (hasValue) {
    return `${label} : ${value}${suffix}`;
  }
  if (extraVisible) {
    return labelWhenEmpty ?? label; // show label even without value
  }
  return '';
}


private pushIf(
    items: FeedLine[],
    label: string,
    value: unknown,
    opts?: 
    { suffix?: string; 
      color?: string, 
      visibleIf?: boolean;   
      labelWhenEmpty?: string; 
    }
  ) {
    const text = this.safeAssign(label, value, opts?.suffix ?? '',  opts?.visibleIf, opts?.labelWhenEmpty);
    if (text) items.push({ text, legendColor: opts?.color });
  }


getFormattedArea(data: any, metric: string): string {
  if (data == null || data === 'N/A' || data === '') return '';
  const raw = String(data);
  // grab the first number (handles NBSP, commas, etc.)
  const m = raw.match(/[+-]?\d+(?:[.,]\d+)?/);
  if (!m) return '';
  const numberPart = Math.ceil(parseFloat(m[0].replace(/,/g, '')));
  const formatted = numberPart.toLocaleString('en-US');
  return `${formatted} ${metric}`;
}


buildFeedSections(config: { type?: string, color?: string }, filteredCollection: any): FeedViewModel {
  console.log("filteredCollection:", filteredCollection);
    const sections: FeedSection[] = []; {

    const defaultSection: FeedSection = {
      show: false,
      className: 'feed-display feed-class',
      items: [],
    };

    const blackSection: FeedSection = {
      show: false,
      className: 'feed-display oil-class',
      style: { 'background-color': 'black', padding: '7px' },
      items: [],
    };
     const ui: FeedUI = {
      showFilterIcon: false,
    };



    switch (config?.type) {
      case 'Tree counting':
        {
        const defaultColor = '#006400';
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, 'Tree crown area', config);
        break;
      }
       case 'Ship': {
         const defaultColor = '#dfee13ff';
         this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, 'Vessel Count', config);
         ui.showFilterIcon = true;  
        break;
      }
      case 'Transhipment': {
        const defaultColor = '#cd0000';
         this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, 'Transhipment Count', config);
         ui.showFilterIcon = true;  
        break;
      }
      case 'Deforestation': {
        const defaultColor = '#F4A460';
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, 'Deforested area', config);
        break;
      }
      case 'Path-SAR': {
        const defaultColor = '#ba5a06';
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Path (unpaved road) detection - SAR", config);
        break;
      }
      case 'Plantation-SAR': {
        const defaultColor = '#e2e90bff';
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Blank spot detection - SAR", config);
        break;
      }

      case 'Oil': {
       const defaultColor = '#FF00FF';
       this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Oil spill area", config);
      break;
      }
      case 'Flood': {
        const defaultColor = '#cd0000';
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Flood extent", config);
        break;
      }
      case 'Path': {
        const defaultColor = '#ba5a06';
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Path (Unmetalled road)", config);
        break;
      }

      case 'Plantation': {
        const defaultColor = '#e2e90bff';
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Plantation area", config);
        break;
      }
    }

    if (defaultSection.show) sections.push(defaultSection);
    if (blackSection.show) sections.push(blackSection);

    return { sections, ui };
}
}



private readonly prettyLabel = (k: string) =>
  k.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());

buildKeyValueSection(
  section: FeedSection,
  filteredCollection: any,
  defaultColor: string,
  emptyFallbackLabel: string,  
  config?: { type?: string },      
) {

let entries: [string, any][] = [];

if (filteredCollection && typeof filteredCollection === 'object') {
  entries = Object.entries(filteredCollection);
} else {
  entries = [];
}
  // Empty -> show a single fallback row
if (entries.length === 0 ) {
    this.pushIf(section.items, emptyFallbackLabel, "", {
      color: defaultColor,
      visibleIf: true,
      labelWhenEmpty: emptyFallbackLabel,
    });
    section.show = true;
    return;
}


for (const [key, value] of entries) {
  console.log("key:", key, "value:", value, typeof value);
  // 🔹 CASE 1: value is an object with a `metric` -> expand it
  if (value && typeof value === 'object' && 'metric' in value) {
    const metric = value.metric;
    for (const [innerKey, innerValue] of Object.entries(value)) {
      if (innerKey === 'metric') continue; // skip metric itself

      const label = this.prettyLabel(innerKey); // e.g. plantable_area -> Plantable Area
      let display: string;

       if (config?.type === 'Oil') {
        display = this.getFormattedArea(innerValue, metric);
       } else if (config?.type === 'Plantation' || config?.type === 'Plantation-SAR') {
       display = this.roundWithUnit(innerValue, 2, metric);
       } else {
       display = String(value ?? '');
     }
      this.pushIf(section.items, label, display, {
        color: defaultColor,
        visibleIf: true,
        labelWhenEmpty: label,
      });
    }
    continue;
  }
  const label = this.prettyLabel(key);
  this.pushIf(section.items, label, value, {
    color: defaultColor,
    visibleIf: true,
    labelWhenEmpty: label,
  });
}
section.show = section.items.length > 0;
}


roundWithUnit(value: any, decimals: number = 2, metric: string): string {
  console.log("roundWithUnit value:", value, "metric:", metric);
  let rounded = parseFloat(value).toFixed(decimals);
  return `${rounded} ${metric}`;
}

public buildTooltipContent(
  props: Record<string, unknown> = {},
  rawLen?: unknown,
  centroidCoordinates?: [number, number] | number[] | string
): string {
  const esc = (v: any) =>
    String(v)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const loc =
    Array.isArray(centroidCoordinates)
      ? centroidCoordinates.join(', ')       // original precision, lon,lat order
      : (centroidCoordinates ?? '');

  const conf = props?.['confidence'];        // bracket access fixes index-signature warning
  const confidenceLine = conf != null ? `<b>Confidence:</b> ${esc(conf)}<br>` : '';
  const lengthLine     = rawLen ? `<b>Vessel length:</b> ${esc(rawLen)}<br>` : '';
  let locationLine = '';
  if (Array.isArray(centroidCoordinates)) {
    locationLine = `<b>Lng:</b> ${esc(centroidCoordinates[0])}<br>` +
                  `<b>Lat:</b> ${esc(centroidCoordinates[1])}<br>`;
  } else if (centroidCoordinates) {
    locationLine = `<b>Location:</b> ${esc(centroidCoordinates)}<br>`;
  }

  return `
    <div style="background-color:black;color:white;padding:5px;max-width:350px;">
      ${confidenceLine}${lengthLine}${locationLine}
    </div>
  `;
}



}

